import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Newspaper, Search, CalendarDays, Menu, X, Building2, Briefcase,
  Palette, UserSearch, Users, ChevronDown, Globe, User, Truck, LogIn
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

export const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const mainLinks = [
    { to: "/", label: "Haberler", icon: Newspaper },
    { to: "/takvim", label: "Fuar Takvimi", icon: CalendarDays },
    { to: "/is-ilanlari", label: "İş İlanları", icon: Briefcase, soon: true },
    { to: "/arama", label: "Arama", icon: Search },
  ];

  const sectorLinks = [
    { to: "/firmalar", label: "Firmalar", icon: Building2, desc: "Stand tasarım firmaları" },
    { to: "/organizatorler", label: "Organizatörler", icon: Globe, desc: "Fuar organizatörleri" },
    { to: "/freelancerlar", label: "Freelancerlar", icon: User, desc: "Tasarımcılar, görselciler" },
    { to: "/tedarikciler", label: "Tedarikçiler", icon: Truck, desc: "Catering, lojistik, hostes" },
  ];

  // Check which portal is logged in
  const hasOrgToken = localStorage.getItem("org_token");
  const hasCompanyToken = localStorage.getItem("token");
  const hasFreelancerToken = localStorage.getItem("freelancer_token");
  const hasSupplierToken = localStorage.getItem("supplier_token");

  const getActivePanel = () => {
    if (hasOrgToken) return { to: "/organizator-panel", label: "Org Panel", icon: Globe };
    if (hasCompanyToken) return { to: "/panel", label: "Panel", icon: Building2 };
    if (hasFreelancerToken) return { to: "/freelancer-panel", label: "Panel", icon: User };
    if (hasSupplierToken) return { to: "/tedarikci-panel", label: "Panel", icon: Truck };
    return null;
  };

  const activePanel = getActivePanel();

  return (
    <header className="sticky top-0 z-50 bg-[#FAFAF9]/95 backdrop-blur-sm border-b border-stone-200" data-testid="navbar">
      {/* Top bar */}
      <div className="border-b border-stone-100 py-1.5">
        <div className="max-w-screen-xl mx-auto px-4 md:px-8 flex items-center justify-between">
          <span className="font-mono text-[9px] uppercase tracking-widest text-stone-400" data-testid="navbar-date">
            {new Date().toLocaleDateString("tr-TR", {
              weekday: "long", day: "numeric", month: "long", year: "numeric",
            })}
          </span>
          <span className="font-mono text-[9px] uppercase tracking-widest text-stone-400 hidden sm:block">
            Fuar & Etkinlik Sektörünün Dijital Gazetesi
          </span>
        </div>
      </div>

      {/* Logo bar — LEFT ALIGNED */}
      <div className="border-b border-stone-200 py-3">
        <div className="max-w-screen-xl mx-auto px-4 md:px-8 flex items-center">
          <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo">
            {/* Logo icon — globe with signal */}
            <div className="w-10 h-10 bg-[#1B4F72] rounded-md flex items-center justify-center relative">
              <Globe className="w-5 h-5 text-white" />
              <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-[#2E86C1] rounded-full border-2 border-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-stone-950" style={{ fontFamily: "'Playfair Display', serif" }}>
                <span className="text-[#1B4F72]">Expo</span> <span className="text-stone-800">Haber</span>
              </h1>
            </div>
          </Link>
        </div>
      </div>

      {/* Navigation bar */}
      <div className="max-w-screen-xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between py-2">
          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
            {mainLinks.map((link) => {
              const Icon = link.icon;
              const active = location.pathname === link.to;
              if (link.soon) {
                return (
                  <span
                    key={link.to}
                    className="flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-stone-400 cursor-not-allowed"
                  >
                    <Icon className="w-3 h-3" />
                    {link.label}
                  </span>
                );
              }
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${
                    active
                      ? "text-[#1B4F72] bg-blue-50"
                      : "text-stone-600 hover:text-stone-900 hover:bg-stone-50"
                  }`}
                  data-testid={`nav-link-${link.to.replace("/", "") || "home"}`}
                >
                  <Icon className="w-3 h-3" />
                  {link.label}
                </Link>
              );
            })}

            <div className="w-px h-4 bg-stone-200 mx-1" />

            {/* Sektör Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-stone-500 hover:text-stone-900 hover:bg-stone-50 transition-colors" data-testid="nav-more-dropdown">
                  <Building2 className="w-3 h-3" />
                  Sektör
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="rounded-none w-64">
                <DropdownMenuLabel className="font-mono text-[9px] uppercase tracking-widest text-stone-400">Sektör Rehberi</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {sectorLinks.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link key={item.to} to={item.to}>
                      <DropdownMenuItem className="font-mono text-xs flex items-center gap-3 cursor-pointer py-2">
                        <Icon className="w-4 h-4 text-[#1B4F72]" />
                        <div>
                          <p className="font-medium">{item.label}</p>
                          <p className="text-[10px] text-stone-400">{item.desc}</p>
                        </div>
                      </DropdownMenuItem>
                    </Link>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-2">
            <Link to="/arama" className="p-2 text-stone-500 hover:text-stone-900 transition-colors" data-testid="nav-search-icon">
              <Search className="w-4 h-4" />
            </Link>
            
            {activePanel ? (
              <Link to={activePanel.to} className="flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-white bg-[#1B4F72] hover:bg-[#154360] transition-colors" data-testid="nav-panel-btn">
                <activePanel.icon className="w-3 h-3" /> {activePanel.label}
              </Link>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-white bg-[#1B4F72] hover:bg-[#154360] transition-colors" data-testid="nav-login-dropdown">
                    <LogIn className="w-3 h-3" /> Giriş Yap
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="rounded-none w-56" align="end">
                  <DropdownMenuLabel className="font-mono text-[9px] uppercase tracking-widest text-stone-400">Portal Seçin</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <Link to="/giris">
                    <DropdownMenuItem className="font-mono text-xs flex items-center gap-2 cursor-pointer">
                      <Building2 className="w-4 h-4 text-blue-600" />
                      <div>
                        <p className="font-medium">Fuar Ajansı</p>
                        <p className="text-[10px] text-stone-400">Stand tasarım firmaları</p>
                      </div>
                    </DropdownMenuItem>
                  </Link>
                  <Link to="/organizator-giris">
                    <DropdownMenuItem className="font-mono text-xs flex items-center gap-2 cursor-pointer">
                      <Globe className="w-4 h-4 text-purple-600" />
                      <div>
                        <p className="font-medium">Organizatör</p>
                        <p className="text-[10px] text-stone-400">Fuar organizatörleri</p>
                      </div>
                    </DropdownMenuItem>
                  </Link>
                  <Link to="/freelancer-giris">
                    <DropdownMenuItem className="font-mono text-xs flex items-center gap-2 cursor-pointer">
                      <User className="w-4 h-4 text-amber-600" />
                      <div>
                        <p className="font-medium">Freelancer</p>
                        <p className="text-[10px] text-stone-400">Tasarımcılar, 3D görselciler</p>
                      </div>
                    </DropdownMenuItem>
                  </Link>
                  <Link to="/tedarikci-giris">
                    <DropdownMenuItem className="font-mono text-xs flex items-center gap-2 cursor-pointer">
                      <Truck className="w-4 h-4 text-teal-600" />
                      <div>
                        <p className="font-medium">Tedarikçi</p>
                        <p className="text-[10px] text-stone-400">Catering, lojistik, vb.</p>
                      </div>
                    </DropdownMenuItem>
                  </Link>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Mobile */}
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        {mobileOpen && (
          <nav className="md:hidden pb-3 border-t border-stone-100 pt-2 space-y-0.5" data-testid="mobile-nav">
            {mainLinks.filter(l => !l.soon).map((link) => {
              const Icon = link.icon;
              const active = location.pathname === link.to;
              return (
                <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 font-mono text-xs uppercase tracking-widest transition-colors ${active ? "text-[#1B4F72] bg-blue-50" : "text-stone-600 hover:bg-stone-50"}`}>
                  <Icon className="w-4 h-4" />{link.label}
                </Link>
              );
            })}
            <Link to="/tedarikciler" onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5 px-3 py-2.5 font-mono text-xs uppercase tracking-widest text-stone-600 hover:bg-stone-50">
              <Truck className="w-4 h-4" />Tedarikçiler
            </Link>
            <div className="border-t border-stone-100 pt-2 mt-2">
              <p className="px-3 py-1 font-mono text-[9px] uppercase tracking-widest text-stone-400">Giriş Yap</p>
              <Link to="/giris" onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5 px-3 py-2 font-mono text-xs text-stone-600 hover:bg-stone-50">
                <Building2 className="w-4 h-4 text-blue-600" />Fuar Ajansı
              </Link>
              <Link to="/organizator-giris" onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5 px-3 py-2 font-mono text-xs text-stone-600 hover:bg-stone-50">
                <Globe className="w-4 h-4 text-purple-600" />Organizatör
              </Link>
              <Link to="/freelancer-giris" onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5 px-3 py-2 font-mono text-xs text-stone-600 hover:bg-stone-50">
                <User className="w-4 h-4 text-amber-600" />Freelancer
              </Link>
              <Link to="/tedarikci-giris" onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5 px-3 py-2 font-mono text-xs text-stone-600 hover:bg-stone-50">
                <Truck className="w-4 h-4 text-teal-600" />Tedarikçi
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};
